// frontend/src/app/pdf-corpus/adapters/http-pdf-corpus.ts
import { HttpClient, HttpErrorResponse, HttpEventType } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { MonoTypeOperatorFunction, Observable, of, throwError } from 'rxjs';
import { catchError, filter, map } from 'rxjs/operators';
import { Globals } from '../../global/globals';
import {
  BatchUploadEvent,
  ExtractionState,
  ExtractionStatusDto,
  PaperMetadata,
  PdfDocument,
  UploadOutcome,
} from '../domain/types';
import { PdfCorpusPort } from '../ports/pdf-corpus.port';

interface UploadOutcomeDto {
  status: 'CREATED' | 'EXISTING' | 'ERROR';
  filename: string;
  document?: PdfDocument;
  errorCode?: string;
  message?: string;
}

interface BatchUploadResponseDto {
  results: UploadOutcomeDto[];
}

@Injectable()
export class HttpPdfCorpus implements PdfCorpusPort {
  private readonly http = inject(HttpClient);
  private readonly globals = inject(Globals);

  private url(groupId: number, suffix = ''): string {
    return `${this.globals.backendUri}/groups/${groupId}/pdfs${suffix}`;
  }

  uploadAll(groupId: number, files: File[]): Observable<BatchUploadEvent> {
    const form = new FormData();
    for (const f of files) {
      form.append('files', f, f.name);
    }
    return this.http
      .post<BatchUploadResponseDto>(this.url(groupId), form, {
        reportProgress: true,
        observe: 'events',
      })
      .pipe(
        map((event): BatchUploadEvent | null => {
          if (event.type === HttpEventType.UploadProgress) {
            const percent = event.total ? Math.round((100 * event.loaded) / event.total) : 0;
            return { kind: 'progress', percent };
          }
          if (event.type === HttpEventType.Response) {
            if (!event.body) {
              throw new Error('batch upload succeeded but server returned empty body');
            }
            const results: UploadOutcome[] = event.body.results.map((r) => {
              if (r.status === 'ERROR') {
                return {
                  status: 'ERROR',
                  filename: r.filename,
                  errorCode: r.errorCode ?? 'INTERNAL_ERROR',
                  message: r.message ?? 'unknown error',
                };
              }
              if (!r.document) {
                throw new Error(`malformed response: ${r.status} outcome missing document`);
              }
              return { status: r.status, filename: r.filename, document: r.document };
            });
            return { kind: 'done', results };
          }
          return null;
        }),
        filter((e): e is BatchUploadEvent => e !== null),
        this.translateError(),
      );
  }

  listForGroup(groupId: number): Observable<PdfDocument[]> {
    return this.http.get<PdfDocument[]>(this.url(groupId)).pipe(this.translateError());
  }

  fetchOne(groupId: number, pdfId: number): Observable<PdfDocument> {
    return this.http.get<PdfDocument>(this.url(groupId, `/${pdfId}`)).pipe(this.translateError());
  }

  fetchBytes(groupId: number, pdfId: number): Observable<Blob> {
    return this.http
      .get(this.url(groupId, `/${pdfId}/content`), { responseType: 'blob' })
      .pipe(this.translateError());
  }

  fetchExtractionState(groupId: number, pdfId: number): Observable<ExtractionState> {
    return this.http.get<ExtractionStatusDto>(this.url(groupId, `/${pdfId}/extraction`)).pipe(
      map((job): ExtractionState => ({ kind: 'JOB', job })),
      catchError((err: unknown) => {
        if (err instanceof HttpErrorResponse && err.status === 404) {
          return of<ExtractionState>({ kind: 'NONE' });
        }
        // Reuse the same error-shape as the rest of the adapter
        if (err instanceof HttpErrorResponse) {
          return throwError(() => new Error(`HTTP ${err.status}: request failed`));
        }
        if (err instanceof Error) return throwError(() => err);
        return throwError(() => new Error(String(err)));
      }),
    );
  }

  triggerExtraction(groupId: number, pdfId: number): Observable<ExtractionStatusDto> {
    return this.http
      .post<ExtractionStatusDto>(this.url(groupId, `/${pdfId}/extraction/retry`), null)
      .pipe(this.translateError());
  }

  fetchPaper(groupId: number, paperId: number): Observable<PaperMetadata> {
    return this.http
      .get<PaperMetadata>(`${this.globals.backendUri}/groups/${groupId}/papers/${paperId}`)
      .pipe(this.translateError());
  }

  materializePdf(groupId: number, paperId: number): Observable<number> {
    return this.http
      .post<{
        pdfId: number;
      }>(`${this.globals.backendUri}/groups/${groupId}/papers/${paperId}/pdf`, null)
      .pipe(
        map((res) => res.pdfId),
        catchError((err: unknown) => {
          if (err instanceof HttpErrorResponse) {
            const msg =
              typeof err.error === 'string' && err.error.trim().length > 0
                ? err.error
                : `HTTP ${err.status}: request failed`;
            return throwError(() => new Error(msg));
          }
          if (err instanceof Error) return throwError(() => err);
          return throwError(() => new Error(String(err)));
        }),
      );
  }

  private translateError<T>(): MonoTypeOperatorFunction<T> {
    return catchError((err: unknown) => {
      if (err instanceof HttpErrorResponse) {
        const msg = err.status === 404 ? 'PDF not found' : `HTTP ${err.status}: request failed`;
        return throwError(() => new Error(msg));
      }
      if (err instanceof Error) return throwError(() => err);
      return throwError(() => new Error(String(err)));
    });
  }
}
